import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

import './dialog.css';

/**
 * A modal on the native <dialog>: showModal() makes the page behind it inert
 * and keeps focus inside, Escape closes it, and so does a click on the
 * backdrop unless `closeOnBackdrop` is false (a secret shown once must not
 * vanish on a stray click). Its content mounts only while open, so a form
 * inside starts fresh.
 */
export function Dialog({ open, onClose, title, description, children, footer, width = 460, closeOnBackdrop = true }: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: number;
  closeOnBackdrop?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    if (open && !el.open) {
      el.showModal();
      /* showModal() focuses the first focusable element, the close button; the first field is where typing starts. */
      el.querySelector<HTMLElement>('.dialog__body input, .dialog__body select, .dialog__body textarea')?.focus();
    }
    if (!open && el.open) el.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="dialog"
      style={{ width: `min(${width}px, calc(100vw - 32px))` }}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      {open ? (
        <div className="dialog__inner">
          <div className="dialog__head">
            <div>
              <h2 className="dialog__title" id={titleId}>
                {title}
              </h2>
              {description ? <p className="dialog__desc">{description}</p> : null}
            </div>
            <button type="button" className="dialog__x" onClick={onClose} aria-label="Close">
              <X aria-hidden="true" />
            </button>
          </div>
          {children ? <div className="dialog__body">{children}</div> : null}
          {footer ? <div className="dialog__foot">{footer}</div> : null}
        </div>
      ) : null}
    </dialog>
  );
}
